import React from 'react'
import { Map, Set, List } from 'immutable'
import _map from 'lodash/map'
import _size from 'lodash/size'
import _isArray from 'lodash/isArray'
import {
  TYPE_CONTENT_PRODUCT,
  TYPE_CONTENT_SERIES,
  TYPE_CONTENT_VIDEO,
  TYPE_CONTENT_PERSON,
  TYPE_CONTENT_TAG,
  TYPE_CONTENT_SERIES_GUIDE,
  TYPE_CONTENT_EPISODE_GUIDE,
  isGuideType,
} from 'services/content-type'
import { URL_GAIA_PLUS } from 'services/url/constants'
import {
  formatSeriesEpisodeTotals,
  formatDuration,
} from 'theme/web-app'
import { isFeatureAllowedWithSubscription } from 'services/subscription'
import Link from 'components/Link'
import { TARGET_BLANK } from 'components/Link/constants'
import Vote, { SIZE_SMALL as VOTE_SIZE_SMALL } from 'components/Vote'
import Watch, { WATCH_RENDER_TYPE_BUTTON } from 'components/Watch'
import { getWatchAccess } from 'services/media'
import PlaylistAddRemove from 'components/PlaylistAddRemove'
import IconV2, { ICON_TYPES } from 'components/Icon.v2'
import { TYPES } from 'components/Button'
import TextSeasonEpisode from 'components/TextSeasonEpisode'
import { isEpisode } from 'services/content'
import TileV2Tags from './Tile.v2Tags'
import TileV2Guests from './Tile.v2Guests'
import { TILE_V2_TAGS_SIZE_SMALL } from './constants'
import {
  getTileUrl,
  handleTileV2OnClick,
  getTileV2ElementClassName,
  isLiveAccessUpgradeToWatch,
} from './utils'

/**
 * The title rendered as a link
 * @param {Object} props - The props
 * @param {String} props.title - The content title
 * @param {String} props.url - The url of the content
 * @param {Function} props.onClick - The onclick function
 * @param {String} props.contentType - The type of content
 * @returns {JSX.Element|null} - The content title as a link
 */
function TitleLink (props) {
  const {
    title,
    url,
    onClick,
    contentType,
  } = props
  const isContentTypeProduct = contentType === TYPE_CONTENT_PRODUCT

  if (!title || !url) {
    return null
  }

  return (
    <Link
      to={url}
      className={`tile-v2__title tile-v2__title--${contentType}`}
      onClick={onClick}
      target={isContentTypeProduct ? TARGET_BLANK : null}
      directLink={isContentTypeProduct}
    >
      {title}
    </Link>
  )
}

/**
 * Renders the season and episode numbers
 * @param {Object} props - The props
 * @param {Number} props.seasonNumber - The season number
 * @param {Number} props.episodeNumber - The episode number
 * @returns {String|null} - If seasonNumber and episodeNumber are null, return null.
 *  Otherwise return the formatted season and episode numbers
 */
function SeasonEpisodeNumbers (props) {
  const { seasonNumber, episodeNumber, isGuide } = props

  return (
    <TextSeasonEpisode
      className={['tile-v2__season-episode-numbers']}
      season={seasonNumber}
      episode={episodeNumber}
      isGuide={isGuide}
    />
  )
}

/**
 * Series season episode in the "Series Title: S1:E1" format
 * @param {Object} props - The props
 * @param {String} props.title - The content title
 * @param {Number} props.seasonNumber - The season number
 * @param {Number} props.episodeNumber - The episode number
 * @returns {JSX.Element|null} Series title combined with the season and episode number
 */
function SeriesSeasonEpisode (props) {
  const {
    title, seasonNumber, episodeNumber, isGuide,
  } = props

  if (!title || !seasonNumber || !episodeNumber) {
    return null
  }

  return (
    <div className="tile-v2__series-season-episode">
      <SeasonEpisodeNumbers
        seasonNumber={seasonNumber}
        episodeNumber={episodeNumber}
        isGuide={isGuide}
      />
      <span className="tile-v2__series-title">
        {`${title} `}
      </span>
    </div>
  )
}

/**
 * Upgrade to watch button for Gaia+
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} Component for upgrade to watch CTA
 */
function UpgradeToWatchButton (props) {
  const { staticText } = props
  return (
    <Link
      className="tile-v2__upgrade-btn"
      to={URL_GAIA_PLUS}
    >
      <span className="tile-v2__upgrade-icon">
        <IconV2 type={ICON_TYPES.PADLOCK} />
      </span>
      <span className="tile-v2__upgrade-text">
        {staticText.get('upgradeWatch')}
      </span>
    </Link>
  )
}

/**
 * Feature not allowed with subscription button
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text
 * @returns {JSX.Element} Component for feature not allowed with subscription CTA
 */
function FeatureNotAllowedButton (props) {
  const { staticText, path } = props
  return (
    <Link
      className="tile-v2__feature-not-allowed"
      to={path}
    >
      <span className="tile-v2__feature-not-allowed-icon">
        <IconV2 type={ICON_TYPES.PLAY} />
      </span>
      <span className="tile-v2__feature-not-allowed-text">
        {staticText.get('watchNow')}
      </span>
    </Link>
  )
}

/**
 * Metadata controls such as the watch button, add to playlist, etc.
 * @param {Object} props - The props
 * @param {JSX.Element} props.watch - The jsx elements for the watch button
 * @param {JSX.Element} props.playlist - The jsx elements for the add to playlist button
 * @param {JSX.Element} props.info - The jsx elements for the info link button
 * @returns {JSX.Element} The metadata action controls
 */
function MetadataControls (props) {
  const { watch, playlist } = props

  return (
    <div className="tile-v2__metadata-controls">
      <div className="tile-v2__watch">
        {watch}
      </div>
      <div className="tile-v2__control-buttons">
        {playlist}
      </div>
    </div>
  )
}

/**
 * The series controls, such as play button, add to playlist, info, etc.
 * @param {Object} props - The props
 * @param {Map} props.upstreamContext - The upstream context
 * @param {Map} props.userNodeInfo - The userNodeInfo for the series, including featured episode
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isLoggedIn - Logged in status for the user
 * @param {Map} props.auth - The user's auth
 * @param {Map} props.data - The data for the series tile
 * @param {Boolean} props.legacyData - Whether or not the data source is coming from a
 * @param {List} props.subscriptions - The user's subscriptions - entitlements
 * @param {String} props.country - The user's country Iso2 code
 *  legacy source (i.e., non member home tile requests)
 * @returns {JSX.Element} - The rendered controls for a series tile
 */
function SeriesControls (props) {
  const {
    upstreamContext = Map(),
    userNodeInfo = Map(),
    staticText = Map(),
    isLoggedIn,
    auth = Map(),
    data = Map(),
    legacyData,
    subscriptions,
    country,
    rowPlacement = null,
    index = null,
    adminTitle,
  } = props
  let featuredEpisode

  if (legacyData) {
    featuredEpisode = Map.isMap(data.get('featuredEpisode')) ? data.get('featuredEpisode', Map()) : Map()
  } else {
    featuredEpisode = Map.isMap(userNodeInfo.get('featuredEpisode')) ? userNodeInfo.get('featuredEpisode', Map()) : Map()
  }

  const featuredEpisodeTitle = featuredEpisode.getIn(['data', 'title'], '')
  const featuredEpisodeId = featuredEpisode.getIn(['data', 'contentId'], -1)
  const featuredEpisodeFeature = featuredEpisode.getIn(['data', 'feature'], Map())
  const featuredEpisodePreview = featuredEpisode.getIn(['data', 'preview'], Map())
  const featuredEpisodeWatchText = staticText.get('watchNow')
  const featuredEpisodePath = featuredEpisode.getIn(['data', 'path'], '') || data.get('url')
  const watchAccess = getWatchAccess({
    userSubscriptions: subscriptions,
    media: featuredEpisodeFeature,
    country,
  })
  const upgradeToWatch = isLiveAccessUpgradeToWatch({
    feature: featuredEpisodeFeature,
    subscriptions,
  })
  const isFeatureAllowed = isFeatureAllowedWithSubscription(featuredEpisodeFeature, auth)
  let seriesWatch = null

  if (upgradeToWatch) {
    seriesWatch = (
      <UpgradeToWatchButton staticText={staticText} />
    )
  }

  if (!isFeatureAllowed && !upgradeToWatch) {
    seriesWatch = (
      <FeatureNotAllowedButton staticText={staticText} path={featuredEpisodePath} />
    )
  }

  if (watchAccess && isFeatureAllowed && !upgradeToWatch) {
    seriesWatch = (
      <Watch
        renderType={WATCH_RENDER_TYPE_BUTTON}
        iconType={ICON_TYPES.CIRCULAR_PLAY}
        upstreamContext={upstreamContext}
        onClickWatch={(e) => handleTileV2OnClick(e, props, true)}
        buttonType={TYPES.ICON}
        buttonClass="tile-v2__watch-button"
        deniedText={featuredEpisodeWatchText}
        feature={featuredEpisodeFeature}
        preview={featuredEpisodePreview}
        text={featuredEpisodeWatchText}
        auth={auth}
        url={featuredEpisodePath}
      />
    )
  }

  const seriesAddPlaylist = (
    <>
      {
        featuredEpisodeId !== -1 && isLoggedIn
          ? <div className="tile-v2__playlist-add-remove" title={staticText.get('addRemove')}>
              <PlaylistAddRemove
                upstreamContext={upstreamContext}
                contentId={featuredEpisodeId}
                contentTitle={featuredEpisodeTitle}
                circlularIcon
                notext
                ga4EventData={{
                  content_id: featuredEpisodeId,
                  content_type: featuredEpisode.getIn(['type', 'content']),
                  content_name: featuredEpisodeTitle,
                  media_id: featuredEpisodeFeature.get('id'),
                  media_language: data.get('mediaLanguage'),
                  is_featured_media: true,
                  parent_series_name: data.get('title'),
                  parent_series_id: data.get('id'),
                  ui_element: 'Tile',
                  row_placement: rowPlacement,
                  tile_number: index,
                  row_name: adminTitle,
                }}
              />
            </div>
          : null
      }
    </>
  )

  return (
    <MetadataControls
      watch={seriesWatch}
      playlist={seriesAddPlaylist}
    />
  )
}

/**
 * The video and episode controls, such as play button, add to playlist, info, etc.
 * @param {Object} props - The props
 * @param {Map} props.upstreamContext - The upstream context
 * @param {Map} props.staticText - The static text
 * @param {Boolean} props.isLoggedIn - Logged in status for the user
 * @param {Map} props.auth - The user's auth
 * @param {Map} props.data - The data for the video or episode tile
 * @param {List} props.subscriptions - The user's subscriptions - entitlements
 * @param {String} props.country - The user's country Iso2 code
 * @returns {JSX.Element} - The rendered controls for a video or episode tile
 */
function VideoEpisodeControls (props) {
  const {
    upstreamContext = Map(),
    staticText = Map(),
    isLoggedIn,
    auth = Map(),
    data = Map(),
    country,
    subscriptions,
    rowPlacement = null,
    index = null,
    adminTitle,
  } = props
  const videoEpisodeTitle = data.get('title', '')
  const videoEpisodeId = data.get('id', -1)
  const videoEpisodeFeature = data.get('feature', Map())
  const videoEpisodePreview = data.get('preview', Map())
  const videoEpisodeWatchText = staticText.get('watchNow')
  const videoEpisodePath = data.get('url', '')
  const watchAccess = getWatchAccess({
    userSubscriptions: subscriptions,
    media: videoEpisodeFeature,
    country,
  })
  const upgradeToWatch = isLiveAccessUpgradeToWatch({
    feature: videoEpisodeFeature,
    subscriptions,
  })
  const isFeatureAllowed = isFeatureAllowedWithSubscription(videoEpisodeFeature, auth)
  let videoEpisodeWatch = null

  if (upgradeToWatch) {
    videoEpisodeWatch = (
      <UpgradeToWatchButton staticText={staticText} />
    )
  }

  if (!isFeatureAllowed && !upgradeToWatch) {
    videoEpisodeWatch = (
      <FeatureNotAllowedButton staticText={staticText} path={videoEpisodePath} />
    )
  }

  if (watchAccess && isFeatureAllowed && !upgradeToWatch) {
    videoEpisodeWatch = (
      <Watch
        renderType={WATCH_RENDER_TYPE_BUTTON}
        iconType={ICON_TYPES.CIRCULAR_PLAY}
        upstreamContext={upstreamContext}
        onClickWatch={(e) => handleTileV2OnClick(e, props, true)}
        buttonType={TYPES.ICON}
        buttonClass="tile-v2__watch-button"
        deniedText={videoEpisodeWatchText}
        feature={videoEpisodeFeature}
        preview={videoEpisodePreview}
        text={videoEpisodeWatchText}
        auth={auth}
        url={videoEpisodePath}
      />
    )
  }

  const videoEpisodeAddPlaylist = (
    <>
      {
        isLoggedIn
          ? <div className="tile-v2__playlist-add-remove" title={staticText.get('addRemove')}>
              <PlaylistAddRemove
                upstreamContext={upstreamContext}
                contentId={videoEpisodeId}
                contentTitle={videoEpisodeTitle}
                circlularIcon
                notext
                ga4EventData={{
                  content_id: videoEpisodeId,
                  content_type: data.get('contentType'),
                  content_name: videoEpisodeTitle,
                  media_id: data.getIn(['feature', 'id']),
                  media_language: data.get('mediaLanguage'),
                  is_featured_media: true,
                  parent_series_name: data.get('seriesTitle'),
                  parent_series_id: data.get('seriesId'),
                  ui_element: 'Tile',
                  row_placement: rowPlacement,
                  tile_number: index,
                  row_name: adminTitle,
                }}
              />
            </div>
          : null
      }
    </>
  )

  return (
    <MetadataControls
      watch={videoEpisodeWatch}
      playlist={videoEpisodeAddPlaylist}
    />
  )
}

/**
 * Audio and subtitle icons
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {Map} props.userNodeInfo - The userNodeInfo for the content
 * @param {List} props.userLanguages - List of the user's languages
 * @param {Boolean} props.legacyData - Whether or not the data source is coming from a
 *  legacy source (i.e., non member home tile requests)
 * @param {String} props.contentType - The type of content
 * @returns {JSX.Element|null} The audio and subtitle icons
 */
function AudioSubtitleIcons (props) {
  const {
    data = Map(),
    userNodeInfo = Map(),
    userLanguages,
    legacyData,
    contentType,
  } = props
  let availableLangs
  let availableSubtitles
  let availableCaptions
  const mediaLang = data.get('primaryLanguage')
  const langIconsNeeded = !userLanguages.includes(mediaLang)

  if (contentType !== TYPE_CONTENT_SERIES && contentType !== TYPE_CONTENT_VIDEO) {
    return null
  }

  if (contentType === TYPE_CONTENT_SERIES && !legacyData) {
    availableLangs = userNodeInfo.getIn(['featuredEpisode', 'media', 'byLang'], Map()).keySeq().toList()
    availableSubtitles = userNodeInfo.getIn(['featuredEpisode', 'media', 'textTracks', 'subtitles'], Map()).keySeq().toList()
    availableCaptions = userNodeInfo.getIn(['featuredEpisode', 'media', 'textTracks', 'captions'], Map()).keySeq().toList()
  }

  if (contentType === TYPE_CONTENT_SERIES && legacyData) {
    const featuredEpisode = Map.isMap(data.get('featuredEpisode')) ? data.get('featuredEpisode', Map()) : Map()
    availableLangs = featuredEpisode.getIn(['media', 'byLang'], Map()).keySeq().toList()
    availableSubtitles = featuredEpisode.getIn(['media', 'textTracks', 'subtitles'], Map()).keySeq().toList()
    availableCaptions = featuredEpisode.getIn(['media', 'textTracks', 'captions'], Map()).keySeq().toList()
  }

  if (contentType === TYPE_CONTENT_VIDEO && !legacyData) {
    availableLangs = userNodeInfo.getIn(['data', 'media', 'byLang'], Map()).keySeq().toList()
    availableSubtitles = userNodeInfo.getIn(['data', 'media', 'textTracks', 'subtitles'], Map()).keySeq().toList()
    availableCaptions = userNodeInfo.getIn(['data', 'media', 'textTracks', 'captions'], Map()).keySeq().toList()
  }

  if (contentType === TYPE_CONTENT_VIDEO && legacyData) {
    const userInfo = Map.isMap(data.get('userInfo')) ? data.get('userInfo', Map()) : Map()
    const userInfoMedia = Map.isMap(userInfo.get('media')) ? userInfo.get('media', Map()) : Map()
    availableLangs = userInfoMedia.get('byLang', Map()).keySeq().toList()
    availableSubtitles = userInfoMedia.getIn(['textTracks', 'subtitles'], Map()).keySeq().toList()
    availableCaptions = userInfoMedia.getIn(['textTracks', 'captions'], Map()).keySeq().toList()
  }

  // if falsy, bail
  if (!availableLangs || !availableCaptions || !availableSubtitles) {
    return null
  }

  // if we don't have the proper data, bail
  if (
    (List.isList(availableLangs) && availableLangs.size === 0)
      || (List.isList(availableCaptions) && availableCaptions.size === 0
        && List.isList(availableSubtitles) && availableSubtitles.size === 0)
  ) {
    return null
  }

  const availableTextTracks = Set([
    ...availableSubtitles,
    ...availableCaptions,
  ])
  const audioOfInterest = langIconsNeeded
    ? availableLangs.some((al) => userLanguages.includes(al))
    : undefined
  const textTrackOfInterest = langIconsNeeded
    ? availableTextTracks.some((att) => userLanguages.includes(att))
    : undefined
  const icons = []

  if (audioOfInterest) {
    icons.push(<IconV2 type={ICON_TYPES.HEADPHONES} key={'headphones'} />)
  }

  if (textTrackOfInterest) {
    icons.push(<IconV2 type={ICON_TYPES.SWITCH_LANGUAGE_OUTLINE} key={'switch-language'} />)
  }

  return icons.length ? icons : null
}

/**
 * The vote/thumbs up component
 * @param {Object} props - The props
 * @param {Map} - The content data
 * @param {Map} - The user's auth
 * @param {Map} - The featured episode
 * @param {Boolean} - If the featured episode is in progress or not
 * @returns {JSX.Element} - The vote/thumbs up component
 */
function VoteReadOnly (props) {
  const {
    data = Map(),
    auth = Map(),
    featuredEpisode = Map(),
    featuredEpisodeInProgress = false,
  } = props
  const vote = data.get('vote', null)
  const voteDown = data.get('voteDown', null)
  const featuredEpisodeVote = Map.isMap(featuredEpisode) ? featuredEpisode.getIn(['data', 'voteUpCount'], 0) : 0
  const featuredEpisodeDownVote = Map.isMap(featuredEpisode) ? featuredEpisode.getIn(['data', 'voteDownCount'], 0) : 0
  const featuredEpisodeId = Map.isMap(featuredEpisode) ? featuredEpisode.getIn(['data', 'contentId'], -1) : -1
  const id = data.get('id')

  return (
    <Vote
      className="vote--right-align vote--small"
      size={VOTE_SIZE_SMALL}
      voteDown={featuredEpisodeInProgress ? featuredEpisodeDownVote : voteDown}
      vote={featuredEpisodeInProgress ? featuredEpisodeVote : vote}
      voteId={featuredEpisodeInProgress ? featuredEpisodeId : id}
      auth={auth}
      upOnly
    />
  )
}

/**
 * Metadata footer, the very bottom of a tile
 * @param {Object} props - The props
 * @param {Array} props.children - The child elements of the component
 * @returns {JSX.Element|null} Wrapped metadata footer elements
 */
function MetadataFooter (props) {
  const { children } = props

  if (!children || !_isArray(children)) {
    return null
  }

  const childrenLength = _size(children)

  return (
    <div className="tile-v2__metadata-footer">
      <div className="tile-v2__metadata-footer-left">
        {
          _map(children, (item, index) => {
            const itemKey = `metadata-item-${index}`

            return (
              <span
                key={itemKey}
                className={`tile-v2__metadata-item${index === childrenLength - 1 ? ' tile-v2__metadata-item--last' : ''}`}
              >
                {item}
              </span>
            )
          })
        }
      </div>
    </div>
  )
}

/**
 * The series metadata
 * @param {Object} props - The props
 * @param {String} props.contentType - The content type
 * @param {Map} props.data - The series data
 * @param {Map} props.staticText - The static text
 * @param {Map} props.userNodeInfo - The userNodeInfo for the series, including featured episode
 * @param {Boolean} props.legacyData - Whether or not the data source is coming from a
 *  legacy source (i.e., non member home tile requests)
 * @returns {JSX.Element} - The series metadata
 */
function SeriesMetadata (props) {
  const {
    contentType,
    data,
    staticText,
    userNodeInfo,
    legacyData,
  } = props
  const title = data.get('title', '')
  const url = getTileUrl(props)
  let featuredEpisode

  if (legacyData) {
    featuredEpisode = Map.isMap(data.get('featuredEpisode')) ? data.get('featuredEpisode', Map()) : Map()
  } else {
    featuredEpisode = Map.isMap(userNodeInfo.get('featuredEpisode')) ? userNodeInfo.get('featuredEpisode', Map()) : Map()
  }

  const featuredEpisodeUserNode = Map.isMap(featuredEpisode.get('userNode')) ? featuredEpisode.get('userNode', Map()) : Map()
  const featuredEpisodeInProgress = featuredEpisodeUserNode.get('featurePosition', 0) > 0
  const featuredEpisodeTitle = featuredEpisode.getIn(['data', 'title'], '')
  const featuredEpisodeUrl = featuredEpisode.getIn(['data', 'path'], '')
  const seasonTotal = data.get('seasonCount')
  const seasonText = staticText.get('season')
  const seasonsText = staticText.get('seasons')
  const lessonsTotal = data.get('episodeCount')
  const lessonText = staticText.get('lesson')
  const lessonsText = staticText.get('lessons')
  const featuredEpisodeDuration = featuredEpisode.getIn(['data', 'feature', 'duration'], 0)
  const featuredEpisodeNumber = featuredEpisode.getIn(['data', 'episode'], 0)
  const featuredEpisodeSeasonNumber = featuredEpisode.getIn(['data', 'season'], 0)

  const isGuide = isGuideType(data)

  return (
    <>
      <SeriesControls {...props} />
      <TitleLink
        title={featuredEpisodeInProgress ? featuredEpisodeTitle : title}
        url={featuredEpisodeInProgress ? featuredEpisodeUrl : url}
        onClick={(e) => handleTileV2OnClick(e, props, false, 'Tile Title')}
        contentType={contentType}
      />
      <TileV2Guests
        {...props}
        useGraphQL={false}
        featuredEpisode={featuredEpisode}
        featuredEpisodeInProgress={featuredEpisodeInProgress}
      />
      {
        featuredEpisodeInProgress
          ? <SeriesSeasonEpisode
              title={title}
              seasonNumber={featuredEpisodeSeasonNumber}
              episodeNumber={featuredEpisodeNumber}
              isGuide={isGuide}
            />
          : null
      }
      <MetadataFooter>
        {
          featuredEpisodeInProgress
            ? formatDuration(featuredEpisodeDuration)
            : formatSeriesEpisodeTotals(
              isGuide ? lessonsTotal : seasonTotal,
              isGuide ? lessonText : seasonText,
              isGuide ? lessonsText : seasonsText,
            )
        }
        <AudioSubtitleIcons {...props} />
        <VoteReadOnly
          {...props}
          featuredEpisode={featuredEpisode}
          featuredEpisodeInProgress={featuredEpisodeInProgress}
        />
      </MetadataFooter>
      <TileV2Tags {...props} size={TILE_V2_TAGS_SIZE_SMALL} />
    </>
  )
}

/**
 * The metadata for videos and episodes
 * @param {Object} props - The props
 * @param {String} props.contentType - The type of content
 * @param {Map} props.data - The content data
 * @returns {JSX.Element} - The video or episode metadata
 */
function VideoEpisodeMetadata (props) {
  const {
    contentType,
    data,
  } = props
  const duration = data.getIn(['feature', 'duration'], 0)
  const title = data.get('title', '')
  const url = getTileUrl(props)
  const season = data.get('season', 0)
  const episode = data.get('episode', 0)
  const seriesTitle = data.get('seriesTitle', '')
  const isGuide = isGuideType(data)

  return (
    <>
      <VideoEpisodeControls {...props} />
      <TitleLink
        title={title}
        url={url}
        onClick={(e) => handleTileV2OnClick(e, props)}
        contentType={contentType}
      />
      <TileV2Guests {...props} useGraphQL={false} />
      {
        isEpisode(props) && season && episode
          ? <SeriesSeasonEpisode
              title={seriesTitle}
              seasonNumber={season}
              episodeNumber={episode}
              isGuide={isGuide}
            />
          : null
      }
      <MetadataFooter>
        {formatDuration(duration)}
        <AudioSubtitleIcons {...props} />
        <VoteReadOnly {...props} />
      </MetadataFooter>
      <TileV2Tags {...props} size={TILE_V2_TAGS_SIZE_SMALL} />
    </>
  )
}

/**
 * A wrapper for the metadata
 * @param {Object} props - The props
 * @returns {JSX.Element} - The wrapped metadata children
 */
function MetadataWrapper (props) {
  const {
    children,
    hovered,
    display,
    contentType,
  } = props

  return (
    <div className={
      getTileV2ElementClassName({
        hovered,
        contentType,
        display,
        baseClassName: 'tile-v2__metadata',
      })}
    >
      {children}
    </div>
  )
}

/**
 * Renders metadata based on the content type.
 * @param {Object} props - The properties object.
 * @param {String} props.contentType - The type of content.
 * @param {Map} props.data - The content data
 * @returns {JSX.Element|null} - The rendered metadata or null.
*/
function Metadata (props) {
  const {
    contentType,
    data,
  } = props
  const title = data.get('title', '')
  const url = getTileUrl(props)

  switch (contentType) {
    case TYPE_CONTENT_SERIES:
    case TYPE_CONTENT_SERIES_GUIDE:
      return (
        <MetadataWrapper {...props}>
          <SeriesMetadata {...props} />
        </MetadataWrapper>
      )
    case TYPE_CONTENT_VIDEO:
    case TYPE_CONTENT_EPISODE_GUIDE:
      return (
        <MetadataWrapper {...props}>
          <VideoEpisodeMetadata {...props} />
        </MetadataWrapper>
      )
    case TYPE_CONTENT_PERSON:
      return (
        <TitleLink
          title={title}
          url={url}
          onClick={(e) => handleTileV2OnClick(e, props)}
          contentType={contentType}
        />
      )
    case TYPE_CONTENT_TAG:
      return (
        <MetadataWrapper {...props}>
          <TitleLink
            title={title}
            url={url}
            onClick={(e) => handleTileV2OnClick(e, props)}
            contentType={contentType}
          />
        </MetadataWrapper>
      )
    case TYPE_CONTENT_PRODUCT:
      return (
        <MetadataWrapper {...props}>
          <TitleLink
            title={title}
            url={url}
            onClick={(e) => handleTileV2OnClick(e, props)}
            contentType={contentType}
          />
        </MetadataWrapper>
      )
    default:
      return null
  }
}

/**
 * The complete rendered metadata for a tile
 * @param {Object} props - The props
 * @returns {JSX.Element} - The metadata for a content tile
 */
function TileV2Metadata (props) {
  return (
    <Metadata {...props} />
  )
}

function tileV2MetadataPropsAreEqual (prevProps, nextProps) {
  const prevData = prevProps.data
  const nextData = nextProps.data
  const prevUserNodeInfo = prevProps.userNodeInfo
  const nextUserNodeInfo = nextProps.userNodeInfo

  return prevData.equals(nextData) && prevUserNodeInfo.equals(nextUserNodeInfo)
}

const MemoizedTileV2Metadata = React.memo(TileV2Metadata, tileV2MetadataPropsAreEqual)

export default MemoizedTileV2Metadata
